import Component from "@glimmer/component";
import { cached, tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import { getOwner } from "@ember/owner";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import didUpdate from "@ember/render-modifiers/modifiers/did-update";
import willDestroy from "@ember/render-modifiers/modifiers/will-destroy";
import { cancel, next, schedule } from "@ember/runloop";
import { service } from "@ember/service";
import { modifier } from "ember-modifier";
import { popupAjaxError } from "discourse/lib/ajax-error";
import discourseDebounce from "discourse/lib/debounce";
import { bind } from "discourse/lib/decorators";
import DiscourseURL from "discourse/lib/url";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import { i18n } from "discourse-i18n";
import ChatChannelStatus from "discourse/plugins/chat/discourse/components/chat-channel-status";
import firstVisibleMessageId from "discourse/plugins/chat/discourse/helpers/first-visible-message-id";
import ChatChannelSubscriptionManager from "discourse/plugins/chat/discourse/lib/chat-channel-subscription-manager";
import {
  FUTURE,
  PAST,
  READ_INTERVAL_MS,
} from "discourse/plugins/chat/discourse/lib/chat-constants";
import ChatMessagesLoader from "discourse/plugins/chat/discourse/lib/chat-messages-loader";
import ChatPaneState from "discourse/plugins/chat/discourse/lib/chat-pane-state";
import { checkMessageTopVisibility } from "discourse/plugins/chat/discourse/lib/check-message-visibility";
import DatesSeparatorsPositioner from "discourse/plugins/chat/discourse/lib/dates-separators-positioner";
import { extractCurrentTopicInfo } from "discourse/plugins/chat/discourse/lib/extract-current-topic-info";
import {
  maintainScrollPosition,
  scrollListToBottom,
  scrollListToMessage,
} from "discourse/plugins/chat/discourse/lib/scroll-helpers";
import ChatMessage from "discourse/plugins/chat/discourse/models/chat-message";
import ChatChannelEmptyState from "./chat/channel/empty-state.gjs";
import ChatComposerChannel from "./chat/composer/channel.js";
import ChatPinnedMessageBar from "./chat/pinned-message-bar.gjs";
import ChatScrollToBottomArrow from "./chat/scroll-to-bottom-arrow.gjs";
import ChatSelectionManager from "./chat/selection-manager.gjs";
import ChatChannelFilter from "./chat-channel-filter.gjs";
import ChatChannelPreviewCard from "./chat-channel-preview-card.gjs";
import ChatMentionWarnings from "./chat-mention-warnings.gjs";
import Message from "./chat-message.gjs";
import ChatMessagesContainer from "./chat-messages-container.gjs";
import ChatMessagesScroller from "./chat-messages-scroller.gjs";
import ChatNotices from "./chat-notices.gjs";
import ChatSkeleton from "./chat-skeleton.gjs";
import ChatUploadDropZone from "./chat-upload-drop-zone.gjs";

export default class ChatChannel extends Component {
  @service capabilities;
  @service chatApi;
  @service chatChannelsManager;
  @service chatNewMessageAnnouncer;
  @service chatDraftsManager;
  @service chatStateManager;
  @service chatChannelScrollPositions;
  @service("chat-channel-composer") composer;
  @service("chat-channel-pane") pane;
  @service currentUser;
  @service dialog;
  @service siteSettings;

  @tracked atBottom = true;
  @tracked uploadDropZone;
  @tracked isScrolling = false;
  // bottom-most visible message, so the pinned bar can anchor to the view
  @tracked lastVisibleMessageId = null;

  scroller = null;

  paneState = new ChatPaneState(getOwner(this), {
    contextKey: this.pendingContextKey,
    onUserPresent: this.maybeDebouncedUpdateLastReadMessage,
  });

  jumpToPinnedMessage = (messageId) => {
    this.highlightOrFetchMessage(messageId, { position: "center" });
  };
  // the overlays only read as raised while list content runs beneath them
  elevateOverlays = modifier((element) => {
    const scroller = element.parentElement.querySelector(
      ".chat-messages-scroller"
    );
    const content = scroller?.querySelector(".chat-messages-container");
    if (!scroller || !content) {
      return;
    }

    // the topmost message, so the loading skeleton and the empty state don't count
    const update = () => {
      const card = element.querySelector(".admin-onboarding-banner__wrap");
      const firstMessage = content.querySelector(".chat-message-container");
      const covered =
        !!card &&
        !!firstMessage &&
        firstMessage.getBoundingClientRect().top <
          card.getBoundingClientRect().bottom;
      element.classList.toggle("--over-content", covered);
    };

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(scroller);
    resizeObserver.observe(content);
    const observeChildren = () => {
      for (const child of element.children) {
        resizeObserver.observe(child);
      }
      update();
    };
    const mutationObserver = new MutationObserver(observeChildren);
    mutationObserver.observe(element, { childList: true });
    observeChildren();
    scroller.addEventListener("scroll", update, { passive: true });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      scroller.removeEventListener("scroll", update);
    };
  });
  // real user input flips this, so onScroll drops the pinned-bar tap override
  // only on a scroll the user drove — not the programmatic jump that set it
  #userScrolled = false;
  #markUserScroll = () => {
    this.#userScrolled = true;
  };
  #userScrollEvents = ["wheel", "touchmove", "pointerdown"];
  _mentionWarningsSeen = {};
  _unreachableGroupMentions = [];
  _overMembersLimitGroupMentions = [];

  @cached
  get messagesLoader() {
    return new ChatMessagesLoader(getOwner(this), this.args.channel);
  }

  get messagesManager() {
    return this.args.channel.messagesManager;
  }

  get isEmpty() {
    return (
      this.messagesLoader.fetchedOnce &&
      this.messagesManager.messages.length === 0
    );
  }

  get currentUserMembership() {
    return this.args.channel?.currentUserMembership;
  }

  get hasSavedScrollPosition() {
    return !!this.chatChannelScrollPositions.get(this.args.channel.id);
  }

  get pendingContextKey() {
    return this.args.channel?.id ? `channel:${this.args.channel.id}` : null;
  }

  @cached
  get hiddenMessageIds() {
    return new Set((this.args.hiddenMessageIds ?? []).map(Number));
  }

  @action
  registerScroller(element) {
    this.scroller = element;
    this.#userScrollEvents.forEach((event) =>
      element.addEventListener(event, this.#markUserScroll, { passive: true })
    );
  }

  @action
  teardown() {
    document.removeEventListener("keydown", this._captureKeystroke);
    this.#userScrollEvents.forEach((event) =>
      this.scroller?.removeEventListener(event, this.#markUserScroll)
    );
    // cleared on teardown (not setup) so a pins-panel click can set it pre-mount
    this.args.channel.activePinnedMessageId = null;
    this.#cancelHandlers();
    this.paneState.teardown();
    this.subscriptionManager.teardown();
    this.updateLastReadMessage();

    // Cancel any pending search request and debounced calls
    cancel(this, this._performSearch);
    this.searchRequest?.abort?.();
    this.searchRequest = null;
  }

  @action
  didResizePane() {
    this.debounceFillPaneAttempt();
    this.maybeDebouncedUpdateLastReadMessage();
    DatesSeparatorsPositioner.apply(this.scroller);

    this.paneState.updatePendingContentFromScrollerPosition({
      scroller: this.scroller,
      fetchedOnce: this.messagesLoader.fetchedOnce,
      canLoadMoreFuture: this.messagesLoader.canLoadMoreFuture,
    });
  }

  @action
  setup(element) {
    this.uploadDropZone = element;

    if (!this.args.disableKeystrokeCapture) {
      document.addEventListener("keydown", this._captureKeystroke);
    }

    this.messagesManager.clear();

    if (
      this.args.channel.isDirectMessageChannel &&
      !this.args.channel.isFollowing
    ) {
      this.chatChannelsManager.follow(this.args.channel);
    }

    this.args.channel.draft =
      this.chatDraftsManager.get(this.args.channel?.id) ||
      ChatMessage.createDraftMessage(this.args.channel, {
        user: this.currentUser,
      });

    if (!this.args.disableAutoFocus) {
      this.composer.focus();
    }

    this.loadMessages();

    // We update this value server-side when we load the Channel
    // here, so this reflects reality for sidebar unread logic.
    this.args.channel.updateLastViewedAt();
  }

  @action
  onLoadTargetMessageId(targetMessageId) {
    this.loadMessages(null, targetMessageId);
  }

  @action
  loadMessages(_element, targetMessageId) {
    targetMessageId ??= this.args.targetMessageId;

    if (!this.args.channel?.id) {
      return;
    }

    this.subscriptionManager = new ChatChannelSubscriptionManager(
      this,
      this.args.channel,
      {
        onNewMessage: this.onNewMessage,
        onRemoveMessage: (message) =>
          this.messagesLoader.releaseMessage(message),
      }
    );

    if (targetMessageId) {
      this.debounceHighlightOrFetchMessage(targetMessageId);
    } else if (this.chatChannelScrollPositions.get(this.args.channel.id)) {
      this.debounceHighlightOrFetchMessage(
        this.chatChannelScrollPositions.get(this.args.channel.id)
      );
    } else {
      this.fetchMessages({ fetch_from_last_read: true });
    }
  }

  @bind
  onNewMessage(message) {
    const isOwnMessage = message.user.id === this.currentUser.id;
    this.messagesLoader.trackLiveMessage(message);

    if (!isOwnMessage) {
      this.chatNewMessageAnnouncer.notify(message, {
        visible: this.paneState.isDocumentVisible,
        active: this.paneState.isActiveReader,
      });
    }

    // the load in flight, or the one that already ran, brings it in
    if (
      !this.messagesLoader.fetchedOnce ||
      this.messagesManager.findMessage(message.id)
    ) {
      return;
    }

    this.paneState.handleIncomingMessage({
      scroller: this.scroller,
      shouldAutoScroll: this.paneState.shouldAutoScrollIncomingMessage({
        isAtLiveEdge: this.paneState.isAtLiveEdge,
        isOwnMessage,
      }),
      canLoadMoreFuture: this.messagesLoader.canLoadMoreFuture,
      addMessage: () => this.messagesManager.addMessages([message]),
      onAutoAdd: () => this.maybeDebouncedUpdateLastReadMessage(),
      isOwnMessage,
    });
  }

  async fetchMessages(findArgs = {}) {
    if (this.messagesLoader.loading) {
      return;
    }

    this.messagesManager.clear();

    const result = await this.messagesLoader.load(findArgs);
    this.messagesManager.messages = this.processMessages(
      this.args.channel,
      result
    );

    if (findArgs.target_message_id) {
      this.scrollToMessageId(findArgs.target_message_id, {
        highlight: true,
        position: findArgs.position,
      });
    } else if (findArgs.fetch_from_last_read) {
      const lastReadMessageId = this.currentUserMembership?.lastReadMessageId;
      this.scrollToMessageId(lastReadMessageId);
    } else if (findArgs.target_date) {
      this.scrollToMessageId(result.meta.target_message_id, {
        highlight: true,
        position: "center",
      });
    } else {
      this._ignoreNextScroll = true;
      this.scrollToBottom();
    }

    this.debounceFillPaneAttempt();
    this.maybeDebouncedUpdateLastReadMessage();
  }

  async fetchMoreMessages({ direction }) {
    if (this.messagesLoader.loading) {
      return;
    }

    const result = await this.messagesLoader.loadMore({ direction });
    if (!result) {
      return;
    }

    // messages held while newer history was unloaded are part of this page
    if (direction === FUTURE && !this.messagesLoader.canLoadMoreFuture) {
      this.paneState.clearPendingMessages();
    }

    this.paneState.updatePendingContentFromScrollerPosition({
      scroller: this.scroller,
      fetchedOnce: this.messagesLoader.fetchedOnce,
      canLoadMoreFuture: this.messagesLoader.canLoadMoreFuture,
    });

    // a request for newer messages made while this page loaded was dropped
    if (direction === PAST && this.paneState.isAtLiveEdge) {
      this.fetchMoreMessages({ direction: FUTURE });
    }

    const messages = this.processMessages(this.args.channel, result);
    if (!messages.length) {
      return;
    }

    if (direction === FUTURE) {
      maintainScrollPosition(this.scroller, () =>
        this.messagesManager.addMessages(messages)
      );
    } else {
      this.messagesManager.addMessages(messages);
    }

    this.debounceFillPaneAttempt();
  }

  @action
  async scrollToBottom() {
    this._ignoreNextScroll = true;
    await scrollListToBottom(this.scroller);
    if (this.paneState.shouldMarkRead()) {
      this.debouncedUpdateLastReadMessage();
    }
    this.paneState.clearPendingMessages();
  }

  scrollToMessageId(messageId, options = {}) {
    this._ignoreNextScroll = true;
    const message = this.messagesManager.findMessage(messageId);
    scrollListToMessage(this.scroller, message, options);
  }

  debounceFillPaneAttempt() {
    this._debouncedFillPaneAttemptHandler = discourseDebounce(
      this,
      this.fillPaneAttempt,
      500
    );
  }

  @bind
  fetchMessagesByDate(date) {
    if (this.messagesLoader.loading) {
      return;
    }

    const message = this.messagesManager.findFirstMessageOfDay(
      new Date(date),
      this.currentUser.user_option.timezone
    );
    if (message.firstOfResults && this.messagesLoader.canLoadMorePast) {
      this.fetchMessages({ target_date: date, direction: FUTURE });
    } else {
      this.highlightOrFetchMessage(message.id, { position: "center" });
    }
  }

  async fillPaneAttempt() {
    if (!this.messagesLoader.fetchedOnce) {
      return;
    }

    // safeguard
    if (this.messagesManager.messages.length > 200) {
      return;
    }

    if (!this.messagesLoader.canLoadMorePast) {
      return;
    }

    schedule("afterRender", () => {
      const firstMessageId = this.messagesManager.messages[0]?.id;
      const messageContainer = this.scroller.querySelector(
        `.chat-message-container[data-id="${firstMessageId}"]`
      );
      if (
        messageContainer &&
        checkMessageTopVisibility(this.scroller, messageContainer)
      ) {
        this.fetchMoreMessages({ direction: PAST });
      }
    });
  }

  @bind
  processMessages(channel, result) {
    const messages = [];
    let foundFirstNew = false;

    const messagesData = (result?.messages ?? []).filter(
      (messageData) => !this.hiddenMessageIds.has(messageData.id)
    );

    // Only compute the newest message marker on a full load.
    // In an already loaded list we need to preserve the "last visit" line.
    const hasNewest = this.messagesManager.messages.some(
      (m) => m.id === channel.newestMessage?.id
    );
    if (!hasNewest) {
      channel.newestMessage = null;
    }

    messagesData.forEach((messageData, index) => {
      messageData.firstOfResults = index === 0;

      if (this.currentUser?.ignored_users) {
        // If a message has been hidden it is because the current user is ignoring
        // the user who sent it, so we want to unconditionally hide it, even if
        // we are going directly to the target
        messageData.hidden = this.currentUser.ignored_users.includes(
          messageData.user.username
        );
      }

      if (this.requestedTargetMessageId === messageData.id) {
        messageData.expanded = !messageData.hidden;
      } else {
        messageData.expanded = !(messageData.hidden || messageData.deleted_at);
      }

      const message = ChatMessage.create(channel, messageData);
      message.manager = channel.messagesManager;

      // newest has to be in after fetch callback as we don't want to make it
      // dynamic or it will make the pane jump around, it will disappear on reload
      if (
        !hasNewest &&
        !foundFirstNew &&
        messageData.id > this.currentUserMembership?.lastReadMessageId
      ) {
        foundFirstNew = true;
        if (message !== channel.lastMessage) {
          channel.newestMessage = message;
        } else {
          channel.newestMessage = null;
        }
      }

      if (message.thread) {
        this.#preloadThreadTrackingState(
          message.thread,
          result.tracking.thread_tracking
        );
      }

      messages.push(message);
    });

    return messages;
  }

  debounceHighlightOrFetchMessage(messageId, options = {}) {
    this._debouncedHighlightOrFetchMessageHandler = discourseDebounce(
      this,
      this.highlightOrFetchMessage,
      messageId,
      options,
      100
    );
  }

  highlightOrFetchMessage(messageId, options = {}) {
    // this jump's own scrolling must not drop a pinned-bar override set for it,
    // so clear any stale user-scroll flag before it starts
    this.#userScrolled = false;
    const message = this.messagesManager.findMessage(messageId);
    if (message) {
      this.scrollToMessageId(
        message.id,
        Object.assign(
          {
            highlight: true,
            position: "start",
            autoExpand: true,
            behavior: this.capabilities.isIOS ? "smooth" : null,
          },
          options
        )
      );
    } else {
      this.fetchMessages({ target_message_id: messageId, position: "end" });
    }
  }

  @bind
  maybeDebouncedUpdateLastReadMessage() {
    if (this.paneState.shouldMarkRead()) {
      this.debouncedUpdateLastReadMessage();
    }
  }

  @bind
  debouncedUpdateLastReadMessage() {
    this._debouncedUpdateLastReadMessageHandler = discourseDebounce(
      this,
      this.updateLastReadMessage,
      READ_INTERVAL_MS
    );
  }

  updateLastReadMessage() {
    if (!this.paneState.shouldMarkRead()) {
      return;
    }

    if (!this.args.channel.isFollowing) {
      return;
    }

    const firstFullyVisibleMessageId = firstVisibleMessageId(this.scroller);
    if (!firstFullyVisibleMessageId) {
      return;
    }

    let firstMessage = this.messagesManager.findMessage(
      firstFullyVisibleMessageId
    );
    if (!firstMessage) {
      return;
    }

    // Clear unread counts optimistically when the latest message is in view.
    if (
      !this.messagesLoader.canLoadMoreFuture &&
      firstMessage.id === this.messagesManager.findLastMessage()?.id
    ) {
      this.args.channel.tracking.unreadCount = 0;
      this.args.channel.tracking.mentionCount = 0;
    }

    const lastReadId =
      this.args.channel.currentUserMembership?.lastReadMessageId;
    if (lastReadId >= firstMessage.id) {
      return;
    }

    // optimistic update
    this.args.channel.currentUserMembership.lastReadMessageId = firstMessage.id;
    this.args.channel.updateLastViewedAt();

    return this.chatApi.markChannelAsRead(
      this.args.channel.id,
      firstMessage.id
    );
  }

  @action
  async scrollToLatestMessage() {
    if (this.messagesLoader.canLoadMoreFuture) {
      await this.fetchMessages();
    } else if (this.messagesManager.messages.length > 0) {
      await this.scrollToBottom();
    }
  }

  @action
  onScroll(state) {
    next(() => {
      if (this.#flushIgnoreNextScroll()) {
        return;
      }

      // re-anchor to the view; drop the tap override only on a user-driven
      // scroll, not the programmatic jump that set it (which fires scroll events
      // too — repeatedly on iOS smooth-scroll and via the fetch reflow)
      this.lastVisibleMessageId = firstVisibleMessageId(this.scroller);
      if (this.#userScrolled) {
        this.#userScrolled = false;
        this.args.channel.activePinnedMessageId = null;
      }

      DatesSeparatorsPositioner.apply(this.scroller);
      this.paneState.updatePendingContentFromScrollState({
        scroller: this.scroller,
        fetchedOnce: this.messagesLoader.fetchedOnce,
        canLoadMoreFuture: this.messagesLoader.canLoadMoreFuture,
        state,
      });
      this.isScrolling = true;
      this.maybeDebouncedUpdateLastReadMessage();

      if (
        state.atTop ||
        (!this.capabilities.isIOS &&
          state.up &&
          state.distanceToTop.percentage < 40)
      ) {
        this.fetchMoreMessages({ direction: PAST });
      } else if (state.atBottom) {
        this.fetchMoreMessages({ direction: FUTURE });
      }
    });
  }

  @action
  onScrollEnd(state) {
    this.isScrolling = false;
    this.atBottom = state.atBottom;
    this.lastVisibleMessageId =
      state.firstVisibleId ?? this.lastVisibleMessageId;
    this.paneState.updateLiveEdgeFromScrollState(state);

    if (state.atBottom) {
      // Visible but unfocused panes can passively live-follow. Clear their
      // pending affordance at the bottom while keeping hidden/away panes pending.
      if (this.paneState.userIsPresent) {
        this.paneState.clearPendingMessages();
      }
      this.fetchMoreMessages({ direction: FUTURE });
      this.chatChannelScrollPositions.delete(this.args.channel.id);
    } else {
      this.paneState.updatePendingContentFromScrollState({
        scroller: this.scroller,
        fetchedOnce: this.messagesLoader.fetchedOnce,
        canLoadMoreFuture: this.messagesLoader.canLoadMoreFuture,
        state,
      });
      this.chatChannelScrollPositions.set(
        this.args.channel.id,
        state.firstVisibleId
      );
    }
  }

  @action
  async onSendMessage(message) {
    if (this.pane.sending) {
      return;
    }

    if (
      message.message.length > this.siteSettings.chat_maximum_message_length
    ) {
      this.dialog.alert(
        i18n("chat.message_too_long", {
          count: this.siteSettings.chat_maximum_message_length,
        })
      );
      return;
    }

    this.pane.sending = true;

    try {
      await message.cook();
      if (message.editing) {
        await this.#sendEditMessage(message);
      } else {
        await this.#sendNewMessage(message);
      }
    } finally {
      this.pane.sending = false;
    }
  }

  @action
  resetComposerMessage() {
    this.args.channel.resetDraft(this.currentUser);
  }

  @action
  resendStagedMessage(stagedMessage) {
    this.pane.sending = true;

    stagedMessage.error = null;

    const data = {
      cooked: stagedMessage.cooked,
      message: stagedMessage.message,
      upload_ids: stagedMessage.uploads.map((upload) => upload.id),
      staged_id: stagedMessage.id,
    };

    this.chatApi
      .sendMessage(this.args.channel.id, data)
      .catch((error) => {
        this._onSendError(data.staged_id, error);
      })
      .finally(() => {
        this.pane.sending = false;
      });
  }

  @action
  onCloseFullScreen() {
    this.chatStateManager.prefersDrawer();

    DiscourseURL.routeTo(this.chatStateManager.lastKnownAppURL).then(() => {
      DiscourseURL.routeTo(this.chatStateManager.lastKnownChatURL);
    });
  }

  async #sendEditMessage(message) {
    const data = {
      message: message.message,
      upload_ids: message.uploads.map((upload) => upload.id),
    };

    this.resetComposerMessage();

    try {
      await this.chatApi.editMessage(this.args.channel.id, message.id, data);
    } catch (e) {
      popupAjaxError(e);
    } finally {
      message.editing = false;
    }
  }

  async #sendNewMessage(message) {
    await this.args.channel.stageMessage(message);

    message.manager = this.args.channel.messagesManager;
    this.resetComposerMessage();

    if (!this.messagesLoader.canLoadMoreFuture) {
      this.scrollToLatestMessage();
    }

    try {
      await this.chatApi.sendMessage(this.args.channel.id, {
        message: message.message,
        in_reply_to_id: message.inReplyTo?.id,
        staged_id: message.id,
        upload_ids: message.uploads.map((upload) => upload.id),
        ...extractCurrentTopicInfo(this),
      });

      this.scrollToLatestMessage();
    } catch (error) {
      this._onSendError(message.id, error);
    }
  }

  #cancelHandlers() {
    cancel(this._debouncedHighlightOrFetchMessageHandler);
    cancel(this._debouncedUpdateLastReadMessageHandler);
    cancel(this._debouncedFillPaneAttemptHandler);
  }

  #preloadThreadTrackingState(thread, threadTracking) {
    if (!threadTracking[thread.id]) {
      return;
    }

    thread.tracking.unreadCount = threadTracking[thread.id].unread_count;
    thread.tracking.mentionCount = threadTracking[thread.id].mention_count;
    thread.tracking.watchedThreadsUnreadCount =
      threadTracking[thread.id].watched_threads_unread_count;
  }

  #flushIgnoreNextScroll() {
    const prev = this._ignoreNextScroll;
    this._ignoreNextScroll = false;
    return prev;
  }

  _onSendError(id, error) {
    const stagedMessage =
      this.args.channel.messagesManager.findStagedMessage(id);
    stagedMessage?.setSendError(error);

    this.resetComposerMessage();
  }

  @bind
  _captureKeystroke(event) {
    const { key, metaKey, ctrlKey, code, target } = event;

    if (
      !key ||
      // Handles things like Enter, Tab, Shift
      key.length > 1 ||
      // Don't need to focus if the user is beginning a shortcut.
      metaKey ||
      ctrlKey ||
      // Space's key comes through as ' ' so it's not covered by key
      code === "Space" ||
      // ? is used for the keyboard shortcut modal
      key === "?"
    ) {
      return;
    }

    if (
      !target ||
      /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName) ||
      target.closest('[contenteditable="true"]')
    ) {
      return;
    }

    event.preventDefault();
    this.composer.focus({ addText: event.key });
    return;
  }

  <template>
    <div
      class={{dConcatClass
        "chat-channel"
        (if this.messagesLoader.loading "loading")
        (if this.pane.sending "chat-channel--sending")
        (if this.hasSavedScrollPosition "chat-channel--saved-scroll-position")
        (if this.messagesLoader.fetchedOnce "--loaded")
        (if this.isEmpty "is-empty")
      }}
      data-id={{@channel.id}}
      {{willDestroy this.teardown}}
      {{didInsert this.setup}}
      {{didUpdate this.loadMessages @targetMessageId}}
    >
      <ChatChannelStatus @channel={{@channel}} />
      <ChatMentionWarnings />
      <ChatChannelFilter
        @channel={{@channel}}
        @isFiltering={{@isFiltering}}
        @onLoadTargetMessageId={{this.onLoadTargetMessageId}}
        @onToggleFilter={{@onToggleFilter}}
      />

      <ChatPinnedMessageBar
        @channel={{@channel}}
        @hiddenMessageIds={{this.hiddenMessageIds}}
        @onJumpToMessage={{this.jumpToPinnedMessage}}
        @viewportBottomMessageId={{this.lastVisibleMessageId}}
      />

      <div class="chat-channel__overlays" {{this.elevateOverlays}}>
        {{yield to="aboveMessages"}}
        <ChatNotices @channel={{@channel}} />
      </div>

      <ChatMessagesScroller
        @onRegisterScroller={{this.registerScroller}}
        @onScroll={{this.onScroll}}
        @onScrollEnd={{this.onScrollEnd}}
      >
        <ChatMessagesContainer @didResizePane={{this.didResizePane}}>
          {{#each this.messagesManager.messages key="id" as |message|}}
            <Message
              @context="channel"
              @disableMouseEvents={{this.isScrolling}}
              @fetchMessagesByDate={{this.fetchMessagesByDate}}
              @message={{message}}
              @resendStagedMessage={{this.resendStagedMessage}}
            />
          {{else}}
            {{#if this.messagesLoader.fetchedOnce}}
              <ChatChannelEmptyState @channel={{@channel}} />
            {{else}}
              <ChatSkeleton />
            {{/if}}
          {{/each}}
        </ChatMessagesContainer>

        {{! at bottom even if shown at top due to column-reverse  }}
        {{#if
          (and
            this.messagesLoader.loadedPast this.messagesManager.messages.length
          )
        }}
          <div class="all-loaded-message">
            {{i18n "chat.all_loaded"}}
          </div>
        {{/if}}
      </ChatMessagesScroller>

      <ChatScrollToBottomArrow
        @channel={{@channel}}
        @isVisible={{this.paneState.hasPendingContentBelow}}
        @onScrollToBottom={{this.scrollToLatestMessage}}
      />

      {{#if this.pane.selectingMessages}}
        <ChatSelectionManager
          @channel={{@channel}}
          @enableMove={{and
            (not @channel.isDirectMessageChannel)
            @channel.canModerate
          }}
          @messagesManager={{this.messagesManager}}
          @pane={{this.pane}}
        />
      {{else}}
        {{#if (and (not @channel.isFollowing) @channel.isCategoryChannel)}}
          <ChatChannelPreviewCard @channel={{@channel}} @context={{@context}} />
        {{else}}
          <ChatComposerChannel
            @channel={{@channel}}
            @onSendMessage={{this.onSendMessage}}
            @scroller={{this.scroller}}
            @uploadDropZone={{this.uploadDropZone}}
          />
        {{/if}}
      {{/if}}

      <ChatUploadDropZone @model={{@channel}} />
    </div>
  </template>
}
